/**
 * Presupuesto: editor de borrador, acciones de estado y vista para el cliente —
 * `TDS-CORE` Parte 2 §2, §4.
 *
 * - **Esta pantalla NO calcula importes.** Los totales, el desglose de IVA y la
 *   retención salen de la API (`URS §129`, `OD-0029`). Mientras se escribe, se
 *   piden a `POST /api/v1/quotes/calculate` medio segundo después de dejar de
 *   teclear (`OD-0062` E-07): los mismos de fiscal-core, sin guardar nada.
 * - Un cliente que no existe se puede crear aquí, sin NIF y con su nombre, como
 *   en Oportunidades (`OD-0062` E-01). Antes se ofrecen los parecidos.
 * - Un presupuesto enviado no se edita (DP-05): se ve como copia congelada, se
 *   acepta, se rechaza, se cancela o se duplica.
 * - La retención se SUGIERE y la decide el usuario (`OD-0029 D-03`).
 * - Clave de idempotencia fija por formulario de alta (`TDS-000 §5.1`).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  api,
  ESTADO_VALIDACION,
  type CalculoPresupuesto,
  type CatalogoIva,
  type Cliente,
  type DatosFiscalesCopia,
  type DatosPresupuesto,
  type ErrorApi,
  type Oportunidad,
  type Presupuesto,
  type Producto,
} from "../api/cliente";
import { t } from "../i18n/es";
import { BloqueActividades, seguimientoDePresupuesto } from "./Actividades";
import { EnviarDocumento } from "./EnviarDocumento";
import { EtiquetaProbabilidad } from "./PantallaOportunidades";
import { TarjetaFacturarPresupuesto } from "./TarjetaFacturarPresupuesto";
import { DocumentoPresupuesto } from "../ui/DocumentoPresupuesto";
import { clientesParecidos, mismoCliente } from "../ui/clientes-parecidos";
import { EditorLineas, LINEA_VACIA, TarjetaTotales } from "../ui/EditorLineas";
import { diaLegible, sinCerosSobrantes } from "../ui/formato";
import { Aviso, Boton, Buscador, Campo, Tarjeta } from "../ui/primitivas";

const VACIO: DatosPresupuesto = {
  customerId: "",
  validUntil: null,
  notes: null,
  withholdingRate: null,
  withholdingReason: null,
  lines: [LINEA_VACIA],
};

/** El texto con el que se busca y se reconoce un cliente. */
function etiquetaCliente(c: Cliente): string {
  return c.taxIdNormalized === null ? c.displayName : `${c.displayName} · ${c.taxIdNormalized}`;
}

function datosDe(q: Presupuesto): DatosPresupuesto {
  return {
    customerId: q.customerId,
    validUntil: q.validUntil,
    notes: q.notes,
    withholdingRate: q.withholdingRate,
    withholdingReason: q.withholdingReason,
    // "12,000" y "250,0000" son correctos, pero nadie los escribe así: en el
    // formulario se editan sin los ceros que no dicen nada. La API acepta las
    // dos formas y sigue siendo ella la que interpreta el número.
    lines:
      q.lines.length === 0
        ? [LINEA_VACIA]
        : q.lines.map((l) => ({
            ...l,
            quantity: sinCerosSobrantes(l.quantity),
            unitPrice: sinCerosSobrantes(l.unitPrice),
            discount: l.discount.kind === "NONE" ? l.discount : { ...l.discount, value: sinCerosSobrantes(l.discount.value) },
          })),
  };
}

/** "" -> null: la API distingue "sin dato" de texto vacío. */
function limpiar(d: DatosPresupuesto): DatosPresupuesto {
  const n = (v: string | null): string | null => (v === null || v.trim() === "" ? null : v);
  return {
    ...d,
    validUntil: n(d.validUntil),
    notes: n(d.notes),
    lines: d.lines.map((l) => ({
      ...l,
      vatRate: l.vatTreatment === "SUJETA_NO_EXENTA" ? l.vatRate : null,
      vatCause: l.vatTreatment === "SUJETA_NO_EXENTA" ? null : l.vatCause,
    })),
  };
}

export function PantallaPresupuesto({
  id,
  hayConexion,
  onVolver,
  onAbrir,
  onIrAEmisor,
  onAbrirFactura,
  onAbrirOportunidad = null,
  onSesionCaducada,
  inicial = null,
}: {
  /** `null` = nuevo borrador. */
  id: string | null;
  /**
   * Un borrador NUEVO que llega con el cliente y el concepto de la primera línea
   * puestos: desde una oportunidad ganada (`OD-0048` D-03). Rellena el
   * formulario y nada más; no se guarda nada hasta que el usuario guarda.
   */
  inicial?: { readonly clienteId: string; readonly concepto: string; readonly oportunidadId?: string } | null;
  hayConexion: boolean;
  onVolver: () => void;
  onAbrir: (id: string) => void;
  onIrAEmisor: () => void;
  /** TDS-CORE Parte 3 §2: el borrador de factura creado desde este presupuesto. */
  onAbrirFactura: (id: string) => void;
  /** OD-0058 D-06: la oportunidad de la que salió, si la hay. */
  onAbrirOportunidad?: ((id: string) => void) | null;
  onSesionCaducada: () => void;
}) {
  const [quote, setQuote] = useState<Presupuesto | null>(null);
  const [oportunidad, setOportunidad] = useState<Oportunidad | null>(null);
  const [datos, setDatos] = useState<DatosPresupuesto>(VACIO);
  const [clientes, setClientes] = useState<readonly Cliente[]>([]);
  const [productos, setProductos] = useState<readonly Producto[]>([]);
  const [catalogo, setCatalogo] = useState<CatalogoIva | null>(null);
  // Para revisar un borrador: el documento necesita un emisor, y la copia
  // congelada no existe hasta que se marca como enviado.
  const [emisor, setEmisor] = useState<DatosFiscalesCopia | null>(null);
  const [cargando, setCargando] = useState(true);
  const [errores, setErrores] = useState<Readonly<Record<string, string>>>({});
  const [erroresLinea, setErroresLinea] = useState<Readonly<Record<string, Readonly<Record<string, string>>>>>({});
  const [mensaje, setMensaje] = useState<{ tono: "exito" | "error" | "aviso" | "info"; texto: string } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [motivoCancelacion, setMotivoCancelacion] = useState("");
  const [imprimiendo, setImprimiendo] = useState(false);
  // El panel «Enviar» de la vista del documento, junto a «Imprimir».
  const [enviando, setEnviando] = useState(false);
  const [faltaEmisor, setFaltaEmisor] = useState(false);
  const [clave] = useState(() => crypto.randomUUID());
  const [claveFactura] = useState(() => crypto.randomUUID());
  // Lo que se ESCRIBE en los buscadores, aparte del id elegido: un texto a
  // medio escribir no es una elección.
  const [busquedaCliente, setBusquedaCliente] = useState("");
  const [busquedaProducto, setBusquedaProducto] = useState<Readonly<Record<number, string>>>({});
  // E-01: el cliente que se crea desde aquí. Una clave por alta: reintentar no crea dos.
  const [claveCliente, setClaveCliente] = useState(() => crypto.randomUUID());
  const [creandoCliente, setCreandoCliente] = useState(false);
  // E-07: lo que dice la API de lo escrito. `null` = todavía nada; `incompleto`,
  // una línea a medias (la API contesta 422 y no hay total que enseñar).
  const [calculo, setCalculo] = useState<{ readonly estado: "ok"; readonly datos: CalculoPresupuesto } | { readonly estado: "incompleto" } | null>(null);
  const [calculando, setCalculando] = useState(false);
  const ultimoCalculo = useRef(0);

  // OD-0058 D-06: la oportunidad enlazada y su probabilidad. Se pide a la API
  // del CRM: el módulo de presupuestos no sabe que existe. Fuera de Business
  // (402), sin permiso o sin oportunidad, no se enseña nada. Se vuelve a pedir
  // al cambiar el estado: aceptarlo sube el porcentaje.
  const estadoPresupuesto = quote?.status ?? null;
  const conCrm = onAbrirOportunidad !== null;
  useEffect(() => {
    if (id === null || !conCrm) return;
    let vigente = true;
    void api.oportunidadDePresupuesto(id).then((r) => {
      if (vigente) setOportunidad(r.ok ? r.datos : null);
    });
    return () => {
      vigente = false;
    };
  }, [id, estadoPresupuesto, conCrm]);

  useEffect(() => {
    void (async () => {
      const [cat, prods, cli, emi, q] = await Promise.all([
        api.catalogoIva(),
        api.listarProductos(),
        api.listarClientes("ACTIVE"),
        api.obtenerEmisor(),
        id === null ? Promise.resolve(null) : api.obtenerPresupuesto(id),
      ]);
      setCargando(false);
      if (cat.ok) setCatalogo(cat.datos);
      if (emi.ok && emi.datos.emisor !== null) {
        const e = emi.datos.emisor;
        setEmisor({
          legalName: e.legalName,
          taxIdNormalized: e.taxIdNormalized,
          addressLine: e.addressLine,
          postalCode: e.postalCode,
          city: e.city,
          province: e.province,
          contactEmail: e.contactEmail,
          contactPhone: e.contactPhone,
          paymentMethod: e.paymentMethod,
          iban: e.iban,
          documentFooter: e.documentFooter,
        });
      }
      if (prods.ok) setProductos(prods.datos.filter((p) => p.active));
      if (cli.ok) setClientes(cli.datos);
      if (q === null) {
        if (inicial !== null) {
          const suyo = (cli.ok ? cli.datos : []).find((c) => c.id === inicial.clienteId);
          setDatos((d) => ({ ...d, customerId: inicial.clienteId, lines: [{ ...LINEA_VACIA, description: inicial.concepto }] }));
          if (suyo !== undefined) setBusquedaCliente(etiquetaCliente(suyo));
        }
        return;
      }
      if (q.ok) {
        setQuote(q.datos);
        setDatos(datosDe(q.datos));
        // El mismo texto que las opciones del buscador: si el usuario lo
        // reescribe igual, sigue coincidiendo con el cliente elegido.
        const suyo = (cli.ok ? cli.datos : []).find((c) => c.id === q.datos.customerId);
        setBusquedaCliente(suyo === undefined ? (q.datos.customer?.displayName ?? "") : etiquetaCliente(suyo));
        const productos = prods.ok ? prods.datos : [];
        setBusquedaProducto(
          Object.fromEntries(
            q.datos.lines.map((l, i) => {
              const p = productos.find((x) => x.id === l.productId);
              return [i, p === undefined ? "" : `${p.code} · ${p.description}`];
            }),
          ),
        );
      } else if (q.estado === 401) onSesionCaducada();
      else setMensaje({ tono: "error", texto: q.estado === 404 ? t.presupuesto.noEncontrado : t.comun.errorGenerico });
    })();
    // `inicial` se lee UNA vez, al abrir el formulario: no es una dependencia.
  }, [id, onSesionCaducada]);

  const editable = quote === null || !quote.frozen;

  // E-07: los totales de lo escrito, un momento después de dejar de escribir.
  // Lo que cuenta para el total: las líneas y la retención, no el cliente.
  const paraCalcular = editable
    ? JSON.stringify({ lines: limpiar(datos).lines, withholdingRate: datos.withholdingRate, withholdingReason: datos.withholdingReason })
    : null;
  useEffect(() => {
    if (paraCalcular === null || cargando) return;
    const n = ++ultimoCalculo.current;
    setCalculando(true);
    const h = setTimeout(() => {
      void api.calcularPresupuesto(JSON.parse(paraCalcular) as DatosPresupuesto).then((r) => {
        if (n !== ultimoCalculo.current) return;
        setCalculando(false);
        if (r.ok) setCalculo({ estado: "ok", datos: r.datos });
        else if (r.estado === 401) onSesionCaducada();
        else if (r.estado === ESTADO_VALIDACION) setCalculo({ estado: "incompleto" });
        // Sin red o sin permiso no se inventa nada: queda lo último que dijo la API.
      });
    }, 500);
    return () => clearTimeout(h);
  }, [paraCalcular, cargando, onSesionCaducada]);

  // E-01: lo escrito que no es ningún cliente. Si ya hay uno con ese nombre
  // (sin tildes ni mayúsculas) o parecidos, se ofrecen antes de crear otro.
  const nombreEscrito = busquedaCliente.trim().replace(/\s+/g, " ");
  const sinElegir = editable && datos.customerId === "" && nombreEscrito !== "";
  const iguales = useMemo(() => (sinElegir ? clientes.filter((c) => mismoCliente(nombreEscrito, c.displayName)) : []), [sinElegir, nombreEscrito, clientes]);
  const candidatos = useMemo(
    () => (sinElegir ? [...iguales, ...clientesParecidos(nombreEscrito, clientes)] : []),
    [sinElegir, iguales, nombreEscrito, clientes],
  );
  const sePuedeCrear = sinElegir && iguales.length === 0;

  function elegirCliente(c: Cliente): void {
    setDatos((d) => ({ ...d, customerId: c.id }));
    setBusquedaCliente(etiquetaCliente(c));
  }

  async function crearCliente(): Promise<void> {
    limpiarAvisos();
    setCreandoCliente(true);
    const r = await api.crearClienteProvisional(nombreEscrito, claveCliente);
    setCreandoCliente(false);
    if (!r.ok) {
      if (r.estado === ESTADO_VALIDACION) return setErrores({ customerId: r.error?.campos.displayName ?? "REQUIRED" });
      // 429: sin plazas de cliente. El mensaje de la API ya dice qué hacer (URS §110).
      if (r.estado === 429 && r.error !== null) return setMensaje({ tono: "aviso", texto: `${r.error.message} ${r.error.action ?? ""}`.trim() });
      if (r.estado === 403) return setMensaje({ tono: "aviso", texto: t.presupuesto.sinPermisoCliente });
      return rechazo(r);
    }
    setClaveCliente(crypto.randomUUID());
    setClientes((l) => [...l, r.datos]);
    elegirCliente(r.datos);
    setMensaje({ tono: "exito", texto: t.presupuesto.clienteCreado(r.datos.displayName) });
  }
  const errorDe = (campo: string): string | null =>
    errores[campo] === undefined ? null : (t.presupuesto.errores[errores[campo]] ?? t.comun.errorGenerico);
  const errorLinea = (i: number, campo: string): string | null => {
    const code = erroresLinea[String(i)]?.[campo];
    return code === undefined ? null : (t.presupuesto.errores[code] ?? t.comun.errorGenerico);
  };

  function limpiarAvisos(): void {
    setMensaje(null);
    setErrores({});
    setErroresLinea({});
    setFaltaEmisor(false);
  }

  function rechazo(r: { estado: number; error: ErrorApi | null }): void {
    if (r.estado === 401) return onSesionCaducada();
    if (r.estado === 403) return setMensaje({ tono: "aviso", texto: t.presupuesto.sinPermiso });
    if (r.estado === ESTADO_VALIDACION && r.error !== null) {
      setErrores(r.error.campos);
      setErroresLinea(r.error.lineas);
      return setMensaje({ tono: "error", texto: r.error.message });
    }
    if (r.estado === 409 && r.error?.motivo === "ISSUER_REQUIRED") {
      setFaltaEmisor(true);
      return setMensaje({ tono: "aviso", texto: t.presupuesto.emisorFalta });
    }
    if (r.estado === 409 && r.error?.motivo === "NOT_DRAFT") return setMensaje({ tono: "aviso", texto: t.presupuesto.noEditable });
    if (r.estado === 409 && r.error?.motivo === "CONCURRENT_UPDATE") return setMensaje({ tono: "aviso", texto: t.presupuesto.conflicto });
    if (r.estado === 409 && r.error !== null) return setMensaje({ tono: "aviso", texto: r.error.message });
    // 429 QUOTA_EXCEEDED: el mensaje de la API ya dice límite y qué hacer (URS §110).
    if (r.estado === 429 && r.error !== null) {
      return setMensaje({ tono: "aviso", texto: `${r.error.message} ${r.error.action ?? ""}`.trim() });
    }
    setMensaje({ tono: "error", texto: r.estado === 0 ? t.comun.sinRed : t.comun.errorGenerico });
  }

  async function guardar(revisar = false): Promise<void> {
    limpiarAvisos();
    setOcupado(true);
    const cuerpo = limpiar(datos);
    const r = quote === null ? await api.crearPresupuesto(cuerpo, clave) : await api.editarPresupuesto(quote.id, cuerpo);
    setOcupado(false);
    if (!r.ok) return rechazo(r);
    const eraAlta = quote === null;
    // OD-0048-ENMIENDA-01 D-E2: el presupuesto nacido de una oportunidad queda
    // enlazado a ella al guardarlo por primera vez. Si el enlace falla, el
    // presupuesto ya existe igual: se avisa y no se deshace nada.
    if (eraAlta && inicial?.oportunidadId !== undefined) {
      const enlace = await api.enlazarPresupuestoOportunidad(inicial.oportunidadId, r.datos.id);
      if (!enlace.ok) setMensaje({ tono: "aviso", texto: t.presupuesto.sinEnlaceOportunidad });
    }
    setQuote(r.datos);
    setDatos(datosDe(r.datos));
    // Revisar exige guardar antes: los importes del documento los calcula la
    // API, y sólo los calcula sobre lo guardado.
    if (revisar) return setImprimiendo(true);
    setMensaje({ tono: "exito", texto: eraAlta ? t.presupuesto.creado : t.presupuesto.guardado });
  }

  async function transicion(accion: "send" | "accept" | "reject" | "cancel", cuerpo: Record<string, unknown> = {}): Promise<void> {
    if (quote === null) return;
    limpiarAvisos();
    setOcupado(true);
    const r = await api.transicionPresupuesto(quote.id, accion, cuerpo);
    setOcupado(false);
    setCancelando(false);
    if (!r.ok) return rechazo(r);
    setQuote(r.datos);
    setDatos(datosDe(r.datos));
    const textos = { send: t.presupuesto.enviado, accept: t.presupuesto.aceptado, reject: t.presupuesto.rechazado, cancel: t.presupuesto.cancelado };
    setMensaje({ tono: "exito", texto: textos[accion] });
  }

  /** §2: una sola vez; si ya tiene factura, se abre la existente. */
  async function facturar(): Promise<void> {
    if (quote === null) return;
    limpiarAvisos();
    setOcupado(true);
    const r = await api.facturarPresupuesto(quote.id, claveFactura);
    setOcupado(false);
    if (r.ok) return onAbrirFactura(r.datos.id);
    const existente = r.error?.detalles.invoiceId;
    if (r.estado === 409 && r.error?.motivo === "ALREADY_INVOICED" && typeof existente === "string") return onAbrirFactura(existente);
    rechazo(r);
  }

  async function duplicar(): Promise<void> {
    if (quote === null) return;
    limpiarAvisos();
    setOcupado(true);
    const r = await api.duplicarPresupuesto(quote.id, crypto.randomUUID());
    setOcupado(false);
    if (!r.ok) return rechazo(r);
    onAbrir(r.datos.id);
  }

  if (cargando) {
    return (
      <Tarjeta titulo={t.presupuesto.tituloNuevo}>
        <p>{t.presupuestos.cargando}</p>
      </Tarjeta>
    );
  }

  // Se envía lo vigente: un borrador (se marca al enviarlo), uno enviado o uno
  // aceptado. Uno rechazado, cancelado o vencido ya no se le manda a nadie.
  const sePuedeEnviar = quote !== null && (quote.status === "DRAFT" || quote.status === "SENT" || quote.status === "ACCEPTED");
  const enviar =
    quote === null ? null : (
      <EnviarDocumento
        documento="quotes"
        id={quote.id}
        asunto={t.enviarDocumento.asuntoPresupuesto(quote.number, quote.issuer?.legalName ?? emisor?.legalName ?? "")}
        cuerpo={t.enviarDocumento.cuerpoPresupuesto(quote.number, quote.issuer?.legalName ?? emisor?.legalName ?? "")}
        correoCliente={clientes.find((c) => c.id === quote.customerId)?.email ?? null}
        telefonoCliente={clientes.find((c) => c.id === quote.customerId)?.phone ?? null}
        paisCliente={clientes.find((c) => c.id === quote.customerId)?.country ?? "ES"}
        hayConexion={hayConexion && !ocupado}
        pendienteDeMarcar={!quote.frozen}
        onMarcarEnviado={() => void transicion("send")}
        onSesionCaducada={onSesionCaducada}
      />
    );

  if (imprimiendo && quote !== null) {
    return (
      <>
        <div className="sin-imprimir">
          <Boton variante="secundario" onClick={() => setImprimiendo(false)}>
            {t.presupuesto.volverAlEditor}
          </Boton>
          <Boton onClick={() => window.print()}>{t.presupuesto.imprimir}</Boton>
          {sePuedeEnviar ? (
            <Boton variante="secundario" onClick={() => setEnviando((v) => !v)}>
              {t.enviarDocumento.boton}
            </Boton>
          ) : null}
        </div>
        {enviando && sePuedeEnviar ? (
          <div className="sin-imprimir panel-enviar">
            <Tarjeta titulo={t.enviarDocumento.titulo}>
              {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
              {enviar}
            </Tarjeta>
          </div>
        ) : null}
        <DocumentoPresupuesto quote={quote} emisorProvisional={emisor} />
      </>
    );
  }

  const titulo = quote === null ? t.presupuesto.tituloNuevo : t.presupuesto.titulo(quote.number);
  // El texto de cada opción es lo que se busca y lo que identifica la elección:
  // se le añade el NIF (o el código) para que dos nombres iguales se distingan.
  // El nombre solo o el NIF también eligen, si señalan a un único cliente.
  const opcionesCliente = clientes.map((c) => ({ id: c.id, texto: etiquetaCliente(c), claves: [c.displayName, c.taxIdNormalized] }));
  // Con el formulario abierto, lo que diga el cálculo en vivo; antes de la primera
  // respuesta, lo guardado, que es lo mismo que hay escrito.
  const totals = editable && calculo !== null ? (calculo.estado === "ok" ? calculo.datos.totales : null) : (quote?.totals ?? null);
  const pendienteRetencion =
    editable && calculo !== null ? calculo.estado === "ok" && calculo.datos.pendienteRetencion : quote?.pendingWithholdingDecision === true;

  return (
    <>
      <Boton variante="secundario" onClick={onVolver}>
        {t.presupuesto.volver}
      </Boton>

      <Tarjeta titulo={titulo}>
        {quote !== null ? (
          <p className="ayuda-neutra">
            {t.presupuestos.estados[quote.status]}
            {" · "}
            {quote.validUntil === null ? t.presupuestos.sinValidez : t.presupuestos.validoHasta(diaLegible(quote.validUntil))}
          </p>
        ) : null}
        {oportunidad !== null && onAbrirOportunidad !== null ? (
          <p className="presupuesto-oportunidad">
            <span>{t.presupuesto.deOportunidad(oportunidad.title)}</span>
            <EtiquetaProbabilidad valor={oportunidad.probability} />
            <Boton variante="terciario" onClick={() => onAbrirOportunidad(oportunidad.id)}>
              {t.presupuesto.verOportunidad}
            </Boton>
          </p>
        ) : null}
        {quote?.status === "EXPIRED" ? <Aviso tono="aviso">{t.presupuesto.caducado}</Aviso> : null}
        {!editable ? <Aviso tono="info">{t.presupuesto.copiaCongelada}</Aviso> : null}
        {clientes.length === 0 && editable ? <Aviso tono="info">{t.presupuesto.sinClientesCrear}</Aviso> : null}

        {editable ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void guardar();
            }}
            noValidate
          >
            <div className="fila-campos">
              <Buscador
                id="pre-cliente"
                etiqueta={t.presupuesto.cliente}
                texto={busquedaCliente}
                opciones={opcionesCliente}
                marcador={t.presupuesto.eligeCliente}
                ayuda={t.presupuesto.ayudaCliente}
                onTexto={setBusquedaCliente}
                onElegir={(cid) => setDatos((d) => ({ ...d, customerId: cid ?? "" }))}
                // Con algo escrito que no es ningún cliente, se dice eso y no
                // «Este dato es obligatorio», que parece que no se ha escrito nada.
                error={
                  (busquedaCliente.trim() !== "" && datos.customerId === "" ? t.presupuesto.clienteNoElegido : null) ??
                  errorDe("customerId")
                }
              />
              {candidatos.length > 0 ? (
                <div className="clientes-parecidos">
                  <p className="ayuda-neutra">{iguales.length > 0 ? t.presupuesto.yaExiste : t.presupuesto.parecidos}</p>
                  <ul>
                    {candidatos.map((c) => (
                      <li key={c.id}>
                        <Boton variante="secundario" onClick={() => elegirCliente(c)}>
                          {etiquetaCliente(c)}
                        </Boton>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {sePuedeCrear ? (
                <div className="clientes-parecidos">
                  <Boton variante={candidatos.length > 0 ? "terciario" : "secundario"} onClick={() => void crearCliente()} deshabilitado={creandoCliente || !hayConexion}>
                    {creandoCliente ? t.presupuesto.creandoCliente : t.presupuesto.crearCliente(nombreEscrito)}
                  </Boton>
                  <p className="ayuda-neutra">{t.presupuesto.crearClienteAyuda}</p>
                </div>
              ) : null}
              <Campo
                id="pre-validez"
                etiqueta={t.presupuesto.validUntil}
                valor={datos.validUntil ?? ""}
                onChange={(v) => setDatos((d) => ({ ...d, validUntil: v }))}
                ayuda={t.presupuesto.ayudaValidUntil}
                // Calendario del navegador: ya es accesible y devuelve
                // AAAA-MM-DD, que es lo que espera la API (D-01: no se
                // improvisa un selector de fechas).
                tipo="date"
                autoComplete="off"
                requerido={false}
                error={errorDe("validUntil")}
              />
            </div>

            <EditorLineas
              prefijo="pre"
              datos={datos}
              setDatos={setDatos}
              catalogo={catalogo}
              productos={productos}
              busquedaProducto={busquedaProducto}
              setBusquedaProducto={setBusquedaProducto}
              totals={totals}
              sugerencia={quote?.withholdingSuggestion?.suggestedRate ?? null}
              pendienteRetencion={pendienteRetencion}
              errorDe={errorDe}
              errorLinea={errorLinea}
            />
            <p className="ayuda-neutra" role="status">
              {calculando ? t.presupuesto.calculando : calculo?.estado === "incompleto" ? t.presupuesto.totalIncompleto : ""}
            </p>

            {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
            {faltaEmisor ? (
              <Boton variante="secundario" onClick={onIrAEmisor}>
                {t.presupuesto.irAEmisor}
              </Boton>
            ) : null}
            {!hayConexion ? <Aviso tono="aviso">{t.comun.sinRed}</Aviso> : null}
            <div className="acciones">
              <Boton tipo="submit" deshabilitado={ocupado || !hayConexion}>
                {ocupado ? t.presupuesto.guardando : t.presupuesto.guardar}
              </Boton>
              <Boton variante="secundario" onClick={() => void guardar(true)} deshabilitado={ocupado || !hayConexion}>
                {t.presupuesto.revisar}
              </Boton>
            </div>
            <p className="ayuda-neutra">{t.presupuesto.ayudaRevisar}</p>
          </form>
        ) : (
          <>
            {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
            {quote !== null ? <DocumentoPresupuesto quote={quote} emisorProvisional={emisor} /> : null}
          </>
        )}
      </Tarjeta>

      {totals !== null ? (
        <TarjetaTotales totals={totals} />
      ) : quote !== null && !editable ? (
        <Tarjeta titulo={t.presupuesto.totalesTitulo}>
          <p className="ayuda-neutra">{t.presupuesto.sinTotales}</p>
        </Tarjeta>
      ) : null}

      {quote !== null ? (
        <Tarjeta titulo={t.presupuesto.vistaImprimible}>
          <Boton variante="secundario" onClick={() => setImprimiendo(true)}>
            {t.presupuesto.imprimir}
          </Boton>
        </Tarjeta>
      ) : null}

      {quote !== null && quote.status === "DRAFT" ? (
        <Tarjeta titulo={t.presupuesto.enviar}>
          <p className="ayuda-neutra">{t.presupuesto.enviarDetalle}</p>
          <p className="ayuda-neutra">{t.enviarDocumento.despuesDeMarcar}</p>
          <Boton onClick={() => void transicion("send")} deshabilitado={ocupado || !hayConexion}>
            {t.presupuesto.enviar}
          </Boton>
        </Tarjeta>
      ) : null}

      {quote !== null && quote.frozen && sePuedeEnviar ? <Tarjeta titulo={t.enviarDocumento.titulo}>{enviar}</Tarjeta> : null}

      {quote !== null && quote.status === "SENT" ? (
        <Tarjeta titulo={t.presupuesto.aceptar}>
          <div className="acciones">
            <Boton onClick={() => void transicion("accept")} deshabilitado={ocupado || !hayConexion}>
              {t.presupuesto.aceptar}
            </Boton>
            <Boton variante="secundario" onClick={() => void transicion("reject")} deshabilitado={ocupado || !hayConexion}>
              {t.presupuesto.rechazar}
            </Boton>
          </div>
        </Tarjeta>
      ) : null}

      {quote !== null && (quote.status === "DRAFT" || quote.status === "SENT") ? (
        <Tarjeta titulo={t.presupuesto.cancelar}>
          {!cancelando ? (
            <Boton variante="secundario" onClick={() => setCancelando(true)} deshabilitado={ocupado || !hayConexion}>
              {t.presupuesto.cancelar}
            </Boton>
          ) : (
            <>
              <Campo
                id="pre-motivo-cancelacion"
                etiqueta={t.presupuesto.motivoCancelar}
                valor={motivoCancelacion}
                onChange={setMotivoCancelacion}
                autoComplete="off"
                error={errorDe("reason")}
              />
              <div className="acciones">
                <Boton onClick={() => void transicion("cancel", { reason: motivoCancelacion })} deshabilitado={ocupado || !hayConexion}>
                  {t.presupuesto.confirmarCancelar}
                </Boton>
                <Boton variante="secundario" onClick={() => setCancelando(false)}>
                  {t.presupuesto.cancelarAccion}
                </Boton>
              </div>
            </>
          )}
        </Tarjeta>
      ) : null}

      {quote !== null ? (
        // OD-0050-ENMIENDA-01: sus tareas. Solo Business; fuera, no se pinta.
        // El seguimiento se propone cuando ya está enviado, que es cuando toca llamar.
        <BloqueActividades
          key={quote.id}
          presupuestoId={quote.id}
          seguimiento={quote.status === "SENT" ? seguimientoDePresupuesto(quote.number) : null}
          onAbrirFactura={onAbrirFactura}
          onSesionCaducada={onSesionCaducada}
        />
      ) : null}

      {quote !== null && quote.status === "ACCEPTED" ? (
        // OD-0053 D-07: «Crear factura» y, en Business, los anticipos y «Facturar el resto».
        <TarjetaFacturarPresupuesto
          quote={quote}
          hayConexion={hayConexion}
          ocupadoFuera={ocupado}
          onCrearFactura={() => void facturar()}
          onAbrirFactura={onAbrirFactura}
          onSesionCaducada={onSesionCaducada}
        />
      ) : null}

      {quote !== null ? (
        <Tarjeta titulo={t.presupuesto.duplicar}>
          <Boton variante="secundario" onClick={() => void duplicar()} deshabilitado={ocupado || !hayConexion}>
            {t.presupuesto.duplicar}
          </Boton>
        </Tarjeta>
      ) : null}
    </>
  );
}
